/**
 * Format time in seconds to m:ss or h:mm:ss if longer
 * @param time time in seconds
 */
import { format } from 'date-fns';

import { isCurrentWeek, isCurrentYear, isToday } from './timeParser';

export const getMinutesFromSeconds = (seconds: number): number =>
	Math.floor(seconds / 60) % 60;

export function timeFormatter(time: number): string {
	time = Math.floor(time);
	const seconds = time % 60;
	const minutes = Math.floor(time / 60) % 60;
	const hours = Math.floor(time / 3600);

	if (hours > 0) {
		return `${hours}:${padTime(minutes)}:${padTime(seconds)}`;
	}

	return `${minutes}:${padTime(seconds)}`;
}

function padTime(value: number): string {
	return `${value}`.padStart(2, '0');
}

function getDiffInSeconds(timestamp: string): number {
	const currentTime = new Date();
	const ref = new Date(timestamp);
	return (currentTime.getTime() - ref.getTime()) / 1000;
}

type OnlineStatusFormatterCallback = (value: number) => string;
export type OnlineStatusFormatter = {
	toSeconds?: OnlineStatusFormatterCallback;
	toMinutes?: OnlineStatusFormatterCallback;
	toHours?: OnlineStatusFormatterCallback;
	toDays?: OnlineStatusFormatterCallback;
};
const defaultFormatter: OnlineStatusFormatter = {
	toSeconds: (value) => 'Active now',
	toMinutes: (value) => `Active ${value} m ago`,
	toHours: (value) => `Active ${value} hr ago`,
};

export function getOnlineStatusToString(
	timestamp: string | undefined,
	formatter?: OnlineStatusFormatter,
): string | undefined {
	if (!timestamp) {
		return undefined;
	}

	if (formatter == null) {
		formatter = defaultFormatter;
	}

	const {
		toSeconds = defaultFormatter.toSeconds,
		toMinutes = defaultFormatter.toMinutes,
		toHours = defaultFormatter.toHours,
		toDays = defaultFormatter.toDays,
	} = formatter;

	const secondsDiff = getDiffInSeconds(timestamp);
	if (secondsDiff < 60) {
		return toSeconds && toSeconds(secondsDiff);
	}
	if (secondsDiff < 60 * 60) {
		const minutes = Math.floor(secondsDiff / 60);

		return toMinutes && toMinutes(minutes);
	}
	if (secondsDiff < 60 * 60 * 24) {
		const hours = Math.floor(secondsDiff / (60 * 60));
		return toHours && toHours(hours);
	}

	const days = Math.floor(secondsDiff / (60 * 60 * 24));
	return toDays && toDays(days);
}

const hrOptionsWithHours: Intl.DateTimeFormatOptions = {
	hour12: false,
	hour: 'numeric',
	minute: 'numeric',
};

/**
 * Converts any Date() compatible string to 24 hours string
 * @param date current date to format
 * @returns time only (ex. 00:00)
 */
export function to24hrString(date: string) {
	const formatter = new Intl.DateTimeFormat('en-GB', hrOptionsWithHours);
	const target = new Date(date);
	return formatter.format(target);
}

/**
 * Converts any Date() compatible string to Day + 24 hours string
 * @param date current date to format
 * @param displayHours decides display time with hours and minutes or not
 * @returns string matching the pattern: DAY Time (ex. SUN 00:00)
 */
export function toDay24hrString(date: string, displayHours = false) {
	const hrOptions = displayHours ? hrOptionsWithHours : {};
	const formatter = new Intl.DateTimeFormat('en-GB', {
		...hrOptions,
		weekday: 'short',
	});

	const target = new Date(date);

	return formatter.format(target);
}

/**
 * Converts any Date() compatible string to Date + 24 hours string
 * @param date current date to format
 * @param displayHours decides display time with hours and minutes or not
 * @returns string matching the pattern: DATE, Time (ex. 03 APR, 00:00)
 */
export function toDate24hrString(date: string, displayHours = false) {
	const hrOptions = displayHours ? hrOptionsWithHours : {};
	const formatter = new Intl.DateTimeFormat('en-GB', {
		...hrOptions,
		day: 'numeric',
		month: 'short',
	});

	const target = new Date(date);

	return formatter.format(target);
}

/**
 * Converts any Date() compatible string to Date + Year + 24 hours string
 * @param date current date to format
 * @param displayHours decides display time with hours and minutes or not
 * @returns string matching the pattern: DATE YYYY, Time (03 APR 2020, 00:00)
 */
export function toYear24hrString(date: string, displayHours = false) {
	const hrOptions = displayHours ? hrOptionsWithHours : {};
	const formatter = new Intl.DateTimeFormat('en-GB', {
		...hrOptions,
		day: 'numeric',
		month: 'short',
		year: 'numeric',
	});

	const target = new Date(date);

	return formatter.format(target);
}

export function isOnline(timestamp: string | undefined) {
	if (timestamp) {
		const lastActionInSeconds = getDiffInSeconds(timestamp);
		return lastActionInSeconds < 60;
	}
	return false;
}

export function getMessageTimestampToString(
	date?: string,
	displayHours = false,
) {
	try {
		if (!date || typeof date === 'string') {
			throw new Error('No date provided');
		}

		let dateFormatted;
		if (isToday(date)) {
			dateFormatted = to24hrString(date);
		} else if (isCurrentWeek(date)) {
			dateFormatted = toDay24hrString(date, displayHours);
		} else if (isCurrentYear(date)) {
			dateFormatted = toDate24hrString(date, displayHours);
		} else {
			dateFormatted = toYear24hrString(date, displayHours);
		}
		return dateFormatted;
	} catch (e) {
		return '';
	}
}

export function getTimePassedAfterTimestamp(date?: string) {
	try {
		if (!date) {
			throw new Error('No date provided');
		}

		let dateFormatted = '';
		const formatter: OnlineStatusFormatter = {
			toMinutes: (value) => `${value}m`,
			toHours: (value) => `${value}h`,
		};
		if (isToday(date)) {
			const {
				toMinutes = defaultFormatter.toMinutes,
				toHours = defaultFormatter.toHours,
			} = formatter;
			const secondsDiff = getDiffInSeconds(date);
			if (secondsDiff < 60) {
				return 'Just now';
			}
			if (secondsDiff < 60 * 60) {
				const minutes = Math.floor(secondsDiff / 60);
				return toMinutes ? toMinutes(minutes) : '';
			}
			if (secondsDiff < 60 * 60 * 24) {
				const hours = Math.floor(secondsDiff / (60 * 60));
				return toHours ? toHours(hours) : '';
			}
		} else if (isCurrentWeek(date)) {
			dateFormatted = toDay24hrString(date, false);
		} else if (isCurrentYear(date)) {
			dateFormatted = toDate24hrString(date, false);
		} else {
			dateFormatted = toYear24hrString(date, false);
		}
		return dateFormatted;
	} catch (e) {
		return '';
	}
}

// hourMinutes must be in format 23:00 and returns 11:00 PM
export const hr24to12 = (hourMinutes24: string) =>
	format(new Date(`1970-01-01T${hourMinutes24}`), 'h:mm a');
